/**
 * Things a soldier does instead of fighting: running for the alarm or for comrades,
 * breaking and fleeing, and dragging a wounded man clear (02 §9.4).
 */

import { bark, raiseAlert, sendToInvestigate } from '../alert';
import { type Ctx, isFighter } from '../ctx';
import { resolveMovement } from '../statics';
import { ENEMY, secs, TACTICS } from '../tuning';
import type { Enemy } from '../types';
import { searchSeconds } from './fight';
import { travel } from './movement';
import { findCoverSpot } from './tactics';

export function stepRunner(ctx: Ctx, e: Enemy): void {
  const w = ctx.world;
  if (travel(ctx, e, e.spotX, e.spotZ, ENEMY.huntSpeed * 1.15, 1.6)) {
    if (w.runnerId === e.id) w.runnerId = -1;
    if (e.runnerPost >= 0 && w.props.find((p) => p.id === e.runnerPost)?.destroyed !== true) {
      // He reached the post: this is what a full alarm is (02 §6.3).
      raiseAlert(ctx, 3, e.targetX, e.targetZ);
      ctx.events.push({ type: 'siren' });
    } else {
      // Fetching comrades: everyone in earshot comes back with him.
      bark(ctx, e, 'help');
      for (const o of w.enemies) {
        if (o.id === e.id) continue;
        if ((o.x - e.x) * (o.x - e.x) + (o.z - e.z) * (o.z - e.z) <= TACTICS.rallyRadius * TACTICS.rallyRadius) {
          sendToInvestigate(o, e.targetX, e.targetZ, true);
          o.wary = true;
        }
      }
    }
    e.runnerPost = -1;
    e.state = 'investigate';
    e.path = [];
  }
}

export function stepFlee(ctx: Ctx, e: Enemy): void {
  e.stateTicks--;
  if (!e.hasSpot) {
    // Toward the nearest friend who is not already in the fight; else simply away.
    const r = ctx.world.ranger;
    let bestD = Infinity;
    for (const o of ctx.world.enemies) {
      if (o.id === e.id || !isFighter(o) || o.state === 'combat' || o.state === 'flee') continue;
      const d = (o.x - e.x) * (o.x - e.x) + (o.z - e.z) * (o.z - e.z);
      if (d > 6 * 6 && d < bestD) {
        bestD = d;
        e.spotX = o.x;
        e.spotZ = o.z;
      }
    }
    if (bestD === Infinity) {
      const ax = e.x - r.x;
      const az = e.z - r.z;
      const l = Math.max(0.01, Math.sqrt(ax * ax + az * az));
      e.spotX = Math.min(ctx.statics.def.sizeX - 2, Math.max(2, e.x + (ax / l) * 18));
      e.spotZ = Math.min(ctx.statics.def.sizeZ - 2, Math.max(2, e.z + (az / l) * 18));
    }
    e.hasSpot = true;
    e.path = [];
  }
  if (travel(ctx, e, e.spotX, e.spotZ, ENEMY.huntSpeed * 1.2, 2) || e.stateTicks <= 0) {
    // He finds his nerve again among friends, and tells them what he saw.
    e.hasSpot = false;
    e.state = 'search';
    e.stateTicks = secs(searchSeconds(ctx));
    e.wary = true;
    bark(ctx, e, 'help');
    for (const o of ctx.world.enemies) {
      if (o.id === e.id) continue;
      if ((o.x - e.x) * (o.x - e.x) + (o.z - e.z) * (o.z - e.z) <= TACTICS.rallyRadius * TACTICS.rallyRadius) sendToInvestigate(o, e.targetX, e.targetZ, true);
    }
  }
}

export function stepRescue(ctx: Ctx, e: Enemy): void {
  const w = ctx.world;
  const casualty = w.enemies.find((o) => o.id === e.carryId);
  const abandon = (): void => {
    if (casualty) casualty.carriedBy = -1;
    e.carryId = -1;
    e.hasSpot = false;
    e.path = [];
    e.state = e.detection >= 1 ? 'combat' : 'search';
    e.stateTicks = secs(searchSeconds(ctx));
  };
  if (!casualty || casualty.state !== 'wounded' || casualty.stabilised || casualty.draggedByRanger) {
    abandon();
    return;
  }
  const dx = casualty.x - e.x;
  const dz = casualty.z - e.z;
  const reached = dx * dx + dz * dz <= 1.3 * 1.3;
  if (!reached && !e.hasSpot) {
    travel(ctx, e, casualty.x, casualty.z, ENEMY.huntSpeed, 1.0);
    return;
  }
  if (!e.hasSpot) {
    // Got him. Now somewhere the ranger cannot see: cover if there is any, else just away.
    const r = w.ranger;
    const cover = findCoverSpot(ctx, e, r.x, r.z);
    if (cover) {
      e.spotX = cover[0];
      e.spotZ = cover[1];
    } else {
      const ax = e.x - r.x;
      const az = e.z - r.z;
      const l = Math.max(0.01, Math.sqrt(ax * ax + az * az));
      e.spotX = Math.min(ctx.statics.def.sizeX - 2, Math.max(2, e.x + (ax / l) * TACTICS.dragDistance));
      e.spotZ = Math.min(ctx.statics.def.sizeZ - 2, Math.max(2, e.z + (az / l) * TACTICS.dragDistance));
    }
    e.hasSpot = true;
    e.path = [];
  }
  const px = e.x;
  const pz = e.z;
  const arrived = travel(ctx, e, e.spotX, e.spotZ, TACTICS.dragSpeed, 1.0);
  // The casualty trails behind the man dragging him.
  const mx = e.x - px;
  const mz = e.z - pz;
  const ml = Math.sqrt(mx * mx + mz * mz);
  if (ml > 1e-4) {
    const trail = resolveMovement(ctx.statics, e.x - (mx / ml) * 0.9, e.z - (mz / ml) * 0.9, 0.3);
    casualty.prevX = casualty.x;
    casualty.prevZ = casualty.z;
    casualty.x = trail.x;
    casualty.z = trail.z;
    casualty.facing = e.facing;
  }
  if (arrived) {
    casualty.stabilised = true;
    abandon();
  }
}

/** Every half second: pair each untended casualty with the best-placed steady hand. */
export function assignRescuers(ctx: Ctx): void {
  const w = ctx.world;
  for (const c of w.enemies) {
    if (c.state !== 'wounded' || c.stabilised || c.carriedBy !== -1 || c.draggedByRanger) continue;
    let best: Enemy | null = null;
    let bestD = TACTICS.rescueReach * TACTICS.rescueReach;
    for (const o of w.enemies) {
      if (!isFighter(o) || o.mans !== null || o.carryId !== -1) continue;
      if (o.persona === 'aggressive' || o.persona === 'jumpy') continue;
      if (o.state === 'runner' || o.state === 'flee' || o.state === 'down' || o.role === 'flank') continue;
      if (o.state !== 'combat' && o.state !== 'search' && o.state !== 'investigate') continue;
      const d = (o.x - c.x) * (o.x - c.x) + (o.z - c.z) * (o.z - c.z);
      if (d < bestD) {
        bestD = d;
        best = o;
      }
    }
    if (best) {
      best.state = 'rescue';
      best.carryId = c.id;
      best.hasSpot = false;
      best.path = [];
      c.carriedBy = best.id;
    }
  }
}
